import { QueueingSubject } from 'queueing-subject';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { WebSocketConnectionFactory } from './websocket.factory';

interface EventWithReason {
  reason: string;
}

interface EventWithData {
  data: string;
}

interface EventWithMessage {
  message?: string;
}

interface Status {
  numberConnected: number;
  isCreated: boolean;
  isPending: boolean
}

export class WebsocketConnection {

  public connectionStatus: BehaviorSubject<Status>;
  private input: QueueingSubject<Record<string, any>>;
  private proxy: Subject<string>;
  private output: Observable<any>;

  constructor(
    private readonly path: string,
    private readonly connectionFactory: WebSocketConnectionFactory,
    private readonly name: string,
  ) {
    this.connectionStatus = new BehaviorSubject<Status>({
      numberConnected: 0,
      isCreated: false,
      isPending: false
    });
  }

  public send(data: Record<string, any>) {
    this.input.next(data);
  }

  public connect(
    options: string[],
    filters: Record<string, any>,
    reset: boolean
  ) {
    this.input = new QueueingSubject<object>();
    if (filters) {
      this.input.next(filters);
    }
    this.connectionStatus = new BehaviorSubject<Status>({
      numberConnected: 0,
      isCreated: false,
      isPending: true
    });
    this.connectionStatus.subscribe();
    this.proxy = new Subject<string>();
    this.output = this.proxy.pipe(
      map(message => JSON.parse(message))
    );

    const socket = this.connectionFactory(this.path, options, this.name, reset);

    let inputSubscription: Subscription;
    let open = false;
    let forcedClose = false;

    const shutdown = () => {
      if (!open) {
        this.connectionStatus.next({
          numberConnected: this.connectionStatus.getValue().numberConnected,
          isCreated: this.connectionStatus.getValue().isCreated,
          isPending: false
        });
        return;
      }
      this.connectionStatus.next({
        numberConnected: this.connectionStatus.getValue().numberConnected - 1,
        isCreated: this.connectionStatus.getValue().isCreated,
        isPending: false
      });

      if (inputSubscription) {
        inputSubscription.unsubscribe();
      }

      open = false;
    };

    socket.onopen = () => {
      this.connectionStatus.next({
        numberConnected: this.connectionStatus.getValue().numberConnected + 1,
        isCreated: this.connectionStatus.getValue().isCreated,
        isPending: false
      });

      open = true;
      inputSubscription = this.input.pipe(
        map(message => JSON.stringify(message))
      ).subscribe(data => {
        socket.send(data);
      });
    };

    socket.onmessage = (message: EventWithData) => {
      this.proxy.next(message.data);
    };

    socket.onerror = (ev: EventWithMessage) => {
      shutdown();
      this.proxy.error(new Error(ev.message ? ev.message : 'unexpected error'));
    };

    socket.onclose = (event: EventWithReason) => {
      if (!open) {
        return;
      }

      shutdown();
      if (forcedClose) {
        this.proxy.complete();
      } else {
        this.proxy.error(new Error(event.reason));
      }
    };

    return new Observable<any>(observer => {
      this.connectionStatus.next({
        numberConnected: this.connectionStatus.getValue().numberConnected,
        isCreated: true,
        isPending: this.connectionStatus.getValue().isPending
      });

      this.output.subscribe(
        data => observer.next(data),
        err => observer.error(err),
        () => observer.complete(),
      );

      return () => {
        forcedClose = true;
        if (inputSubscription) {
          inputSubscription.unsubscribe();
        }

        if (open) {
          shutdown();
          socket.close();
        }
      };
    });
  }
}
